/**
 * Hardcoded Firebase configurations for each environment.
 *
 * These configs are used by:
 * 1. Vite plugin (Node.js) - to exchange refresh tokens for ID tokens
 * 2. Browser SDK - to initialize Firebase for Firestore access
 *
 * SECURITY NOTE: Firebase web API keys are designed to be public. They are NOT
 * secret keys. Security is enforced via:
 * - Firebase Security Rules (control read/write access)
 * - Authentication (users must sign in)
 * - HTTP referrer restrictions (configure in Firebase Console)
 *
 * These same keys are already shipped in the CLI binary (cli/venus_cli/Constants.cs).
 * Google's documentation explicitly states that Firebase API keys are safe to
 * expose in client-side code.
 *
 * TODO: Consider reading Firebase config from ~/.venus/config.json instead of
 * hardcoding here. This would require updating the CLI (cli/venus_cli) to write the config
 * for the selected environment. Benefits: SDK wouldn't ship with keys, config
 * would come from the authenticated CLI.
 */

import type { PlaygroundTarget, PlaygroundFirebaseConfig } from '../config/playground'

/**
 * Firebase configuration for each environment.
 * These values come from the Firebase console for each project.
 */
export const FIREBASE_CONFIGS: Record<PlaygroundTarget, PlaygroundFirebaseConfig> = {
  // Local mode uses dev Firebase credentials (auth emulator doesn't support Google OAuth)
  local: {
    apiKey: 'AIzaSyCSYBs5QrFUeaE8QC2Jfp6834enCzbCJqQ',
    authDomain: 'dev-venus-app.firebaseapp.com',
    projectId: 'dev-venus-app',
    storageBucket: 'dev-venus-app.appspot.com',
  },
  dev: {
    apiKey: 'AIzaSyCSYBs5QrFUeaE8QC2Jfp6834enCzbCJqQ',
    authDomain: 'dev-venus-app.firebaseapp.com',
    projectId: 'dev-venus-app',
    storageBucket: 'dev-venus-app.appspot.com',
  },
  staging: {
    apiKey: 'AIzaSyCfv4HGtNjStUsN0IL-TUNLYSNSZ3KhXN4',
    authDomain: 'staging-venus-app.firebaseapp.com',
    projectId: 'staging-venus-app',
    storageBucket: 'staging-venus-app.appspot.com',
  },
  playground: {
    apiKey: 'AIzaSyAYSjaXmZKeWvAd7kBm0SbqmIbp9lMlVVc',
    authDomain: 'playground-venus-app.firebaseapp.com',
    projectId: 'playground-venus-app',
    storageBucket: 'playground-venus-app.firebasestorage.app',
  },
}

/**
 * Cloud Functions region for each environment.
 */
export const FUNCTIONS_REGIONS: Record<PlaygroundTarget, string> = {
  local: 'us-central1',
  dev: 'us-central1',
  staging: 'us-central1',
  playground: 'us-central1',
}

/**
 * Game Manager base URLs for each environment.
 * Used to auto-create games from the playground playground (no CLI required).
 */
export const GAME_MANAGER_URLS: Partial<Record<PlaygroundTarget, string>> = {
  playground: 'https://venus-server-playground-623315892370.us-central1.run.app',
}

/**
 * Cloud Run base URLs for each environment.
 * Used by HttpUgcApi and other services that call Cloud Run directly.
 */
export const CLOUD_RUN_URLS: Record<PlaygroundTarget, string> = {
  local: 'http://localhost:3000',
  dev: 'https://venus-server-development-575021869122.us-central1.run.app',
  staging: 'https://venus-server-staging-514242076747.us-central1.run.app',
  playground: 'https://venus-server-playground-623315892370.us-central1.run.app',
}

/**
 * Hosted multiplayer room-server (mp-room-server) base URLs. Only `playground`
 * is wired: it lets the SDK playground connect real Syncplay/GameRoom clients to
 * the hosted room server instead of the local dev sidecar. Other targets run the
 * local sidecar in dev, so they intentionally have no hosted URL here.
 */
export const ROOM_SERVER_URLS: Partial<Record<PlaygroundTarget, string>> = {
  playground: 'https://mp-room-server-playground-623315892370.us-central1.run.app',
}

/**
 * RevenueCat Web Billing public API keys for each environment.
 *
 * Public keys (safe to ship in client bundles). Used by playground `HttpIapApi`
 * to configure `@revenuecat/purchases-js` for subscription offerings,
 * checkout, and customer info reads. Webhooks fire against the same RC
 * account; environment is differentiated by RC's `environment: SANDBOX`
 * flag on the webhook payload.
 *
 * Falls back to `process.env.RUNDOT_REVENUECAT_WEB_BILLING_KEY` when the
 * per-target entry is empty.
 */
export const REVENUECAT_WEB_BILLING_KEYS: Record<PlaygroundTarget, string> = {
  local: 'rcb_sb_cnJLlmycPdlrSBiVeauEimOsX',
  dev: 'rcb_sb_cnJLlmycPdlrSBiVeauEimOsX',
  staging: 'rcb_sb_cnJLlmycPdlrSBiVeauEimOsX',
  playground: 'rcb_sb_cnJLlmycPdlrSBiVeauEimOsX',
}

/**
 * Default emulator hosts for local development.
 */
export const DEFAULT_EMULATOR_HOSTS = {
  functions: 'localhost:5001',
  firestore: 'localhost:8080',
  auth: 'localhost:9099',
}

/**
 * Get Firebase config for a target environment.
 */
export function getFirebaseConfig(target: PlaygroundTarget): PlaygroundFirebaseConfig {
  const config = FIREBASE_CONFIGS[target]
  if (!config) {
    throw new Error(`[RUN] Unknown target environment: ${target}`)
  }
  return config
}

/**
 * Get Cloud Functions region for a target environment.
 */
export function getFunctionsRegion(target: PlaygroundTarget): string {
  return FUNCTIONS_REGIONS[target] || 'us-central1'
}
